import { useMemo, useState, type ReactNode } from 'react';
import {
  Eye,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Search,
  Inbox,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { useViewMode } from '../../context/ViewModeContext';

// ─── Types ──────────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc';

export interface EntityListColumn<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  /** Hide on mobile */
  hideOnMobile?: boolean;
  /** Column width class (e.g. 'w-24') */
  width?: string;
  /** Cell + header text alignment */
  align?: 'left' | 'right' | 'center';
  /** Make this column sortable (header becomes clickable) */
  sortable?: boolean;
  /** Sort key sent to parent / used for state. Defaults to `key`. */
  sortKey?: string;
  /** Accessor for client-side (uncontrolled) sorting. */
  sortValue?: (item: T) => string | number | Date | null | undefined;
}

export interface EntityListProps<T> {
  /** Data items to display */
  items: T[];
  /** Column definitions */
  columns: EntityListColumn<T>[];
  /** Unique key extractor */
  getKey: (item: T) => string;
  /** Loading state */
  isLoading?: boolean;
  /** Currently selected item ID */
  selectedId?: string | null;

  // ─── Actions ─────────────────────
  onPreview?: (item: T) => void;
  onEdit?: (item: T) => void;
  onRowClick?: (item: T) => void;

  // ─── Pagination ──────────────────
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  /** Show a page-size selector. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;

  // ─── Sort ────────────────────────
  /** Controlled sort column (use with `onSortChange`). */
  sortBy?: string | null;
  /** Controlled sort direction. */
  sortDir?: SortDirection | null;
  /** Called when a sortable header is clicked. Receives the next state. */
  onSortChange?: (sortBy: string | null, sortDir: SortDirection | null) => void;
  /** Initial sort when uncontrolled. */
  defaultSortBy?: string;
  defaultSortDir?: SortDirection;

  // ─── Search ──────────────────────
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // ─── Custom ──────────────────────
  filterBar?: ReactNode;
  renderCard?: (item: T) => ReactNode;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  title?: string;
  titleActions?: ReactNode;
  /** Skeleton rows shown during loading (defaults to 5). */
  skeletonRows?: number;
}

// ─── Helpers ────────────────────────────────────────────────────

function getColumnSortKey<T>(col: EntityListColumn<T>): string {
  return col.sortKey ?? col.key;
}

function isNil(v: unknown): boolean {
  return v === null || v === undefined;
}

function compareValues(a: string | number | Date, b: string | number | Date): number {
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function nextSortDir(current: SortDirection | null): SortDirection | null {
  if (current === null) return 'asc';
  if (current === 'asc') return 'desc';
  return null;
}

function alignClass(align?: 'left' | 'right' | 'center'): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

// ─── Component ──────────────────────────────────────────────────

export function EntityList<T>({
  items,
  columns,
  getKey,
  isLoading,
  selectedId,
  onPreview,
  onEdit,
  onRowClick,
  page = 1,
  pageSize = 25,
  total,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  sortBy,
  sortDir,
  onSortChange,
  defaultSortBy,
  defaultSortDir,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filterBar,
  renderCard,
  emptyMessage = 'No items found',
  emptyIcon,
  title,
  titleActions,
  skeletonRows = 5,
}: EntityListProps<T>) {
  const { isMobile } = useViewMode();

  // ─── Sort state ──────────────────
  const isSortControlled = onSortChange !== undefined;
  const [internalSortBy, setInternalSortBy] = useState<string | null>(defaultSortBy ?? null);
  const [internalSortDir, setInternalSortDir] = useState<SortDirection | null>(
    defaultSortBy ? (defaultSortDir ?? 'asc') : null,
  );
  const activeSortBy = isSortControlled ? (sortBy ?? null) : internalSortBy;
  const activeSortDir = isSortControlled ? (sortDir ?? null) : internalSortDir;

  const handleSort = (col: EntityListColumn<T>) => {
    if (!col.sortable) return;
    const key = getColumnSortKey(col);
    const isActive = activeSortBy === key;
    const next = isActive ? nextSortDir(activeSortDir) : 'asc';
    const nextKey = next === null ? null : key;
    if (isSortControlled) {
      onSortChange!(nextKey, next);
    } else {
      setInternalSortBy(nextKey);
      setInternalSortDir(next);
    }
  };

  // ─── Apply client-side sort if uncontrolled and column has sortValue ─
  const displayItems = useMemo(() => {
    if (isSortControlled || !activeSortBy || !activeSortDir) return items;
    const col = columns.find((c) => getColumnSortKey(c) === activeSortBy);
    if (!col || !col.sortValue) return items;
    const accessor = col.sortValue;
    const sign = activeSortDir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      // Nil values always sort to the end, regardless of direction.
      const aNil = isNil(va);
      const bNil = isNil(vb);
      if (aNil && bNil) return 0;
      if (aNil) return 1;
      if (bNil) return -1;
      return sign * compareValues(va as string | number | Date, vb as string | number | Date);
    });
  }, [items, columns, activeSortBy, activeSortDir, isSortControlled]);

  const totalPages = total ? Math.ceil(total / pageSize) : 1;
  const showPagination = total != null && totalPages > 1;
  const showPageSizeSelector =
    pageSizeOptions !== undefined && pageSizeOptions.length > 0 && onPageSizeChange !== undefined;

  // ─── Loading State (skeleton) ─────
  if (isLoading) {
    return (
      <div data-testid="entity-list-loading">
        <span className="sr-only" role="status">
          Loading...
        </span>
        {(title || titleActions) && (
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            {title && (
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                {title}
              </h3>
            )}
            {titleActions && (
              <div className="flex items-center gap-2 flex-1 justify-end">{titleActions}</div>
            )}
          </div>
        )}
        {isMobile ? (
          <div className="space-y-2">
            {Array.from({ length: skeletonRows }).map((_, i) => (
              <div
                key={i}
                className="p-3 rounded-xl border border-gray-200 bg-white animate-pulse"
                data-testid="entity-list-skeleton-row"
              >
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${alignClass(col.align)} ${col.width || ''}`}
                    >
                      {col.header}
                    </th>
                  ))}
                  {(onPreview || onEdit) && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Array.from({ length: skeletonRows }).map((_, i) => (
                  <tr key={i} data-testid="entity-list-skeleton-row" className="animate-pulse">
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <div className="h-3 bg-gray-200 rounded w-3/4" />
                      </td>
                    ))}
                    {(onPreview || onEdit) && (
                      <td className="px-4 py-3">
                        <div className="h-3 bg-gray-200 rounded w-12 ml-auto" />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ─── Sort indicator for header ────
  const renderSortIndicator = (col: EntityListColumn<T>) => {
    if (!col.sortable) return null;
    const isActive = activeSortBy === getColumnSortKey(col);
    if (!isActive) return <ChevronsUpDown size={12} className="text-gray-300" />;
    return activeSortDir === 'asc' ? (
      <ChevronUp size={12} className="text-primary-600" />
    ) : (
      <ChevronDown size={12} className="text-primary-600" />
    );
  };

  // ─── Mobile sort toolbar ──────────
  const sortableColumns = columns.filter((c) => c.sortable);
  const showMobileSortToolbar = isMobile && sortableColumns.length > 0;

  const handleMobileSortKeyChange = (key: string) => {
    if (key === '') {
      if (isSortControlled) onSortChange!(null, null);
      else {
        setInternalSortBy(null);
        setInternalSortDir(null);
      }
      return;
    }
    const dir: SortDirection = activeSortDir ?? 'asc';
    if (isSortControlled) onSortChange!(key, dir);
    else {
      setInternalSortBy(key);
      setInternalSortDir(dir);
    }
  };

  const handleMobileSortDirToggle = () => {
    if (!activeSortBy) return;
    const dir: SortDirection = activeSortDir === 'asc' ? 'desc' : 'asc';
    if (isSortControlled) onSortChange!(activeSortBy, dir);
    else setInternalSortDir(dir);
  };

  return (
    <div>
      {/* Header: title + search + actions */}
      {(title || searchValue !== undefined || titleActions) && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          {title && (
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              {title}
              {total != null && (
                <span className="text-sm font-normal text-gray-400">({total})</span>
              )}
            </h3>
          )}
          <div className="flex items-center gap-2 flex-1 justify-end">
            {searchValue !== undefined && onSearchChange && (
              <div className="relative max-w-xs flex-1 sm:flex-none sm:w-56">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}
            {titleActions}
          </div>
        </div>
      )}

      {/* Filter bar */}
      {filterBar && <div className="mb-4">{filterBar}</div>}

      {/* Mobile sort toolbar */}
      {showMobileSortToolbar && items.length > 0 && (
        <div className="flex items-center gap-2 mb-3" data-testid="entity-list-mobile-sort-toolbar">
          <label className="text-xs text-gray-500">Sort:</label>
          <select
            aria-label="Sort by"
            value={activeSortBy ?? ''}
            onChange={(e) => handleMobileSortKeyChange(e.target.value)}
            className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">None</option>
            {sortableColumns.map((c) => (
              <option key={c.key} value={getColumnSortKey(c)}>
                {c.header}
              </option>
            ))}
          </select>
          <button
            type="button"
            aria-label="Toggle sort direction"
            disabled={!activeSortBy}
            onClick={handleMobileSortDirToggle}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
          >
            {activeSortDir === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      )}

      {/* Empty state */}
      {displayItems.length === 0 ? (
        <div className="py-16 flex flex-col items-center gap-3">
          {emptyIcon || <Inbox size={40} className="text-gray-300" />}
          <p className="text-sm text-gray-400">{emptyMessage}</p>
        </div>
      ) : isMobile ? (
        /* ─── Mobile: Card Layout ───── */
        <div className="space-y-2">
          {displayItems.map((item) => {
            const key = getKey(item);
            const isSelected = selectedId === key;

            if (renderCard) {
              return (
                <div
                  key={key}
                  onClick={() => onRowClick?.(item)}
                  className={`rounded-xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-primary-400 bg-primary-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  {renderCard(item)}
                </div>
              );
            }

            return (
              <div
                key={key}
                onClick={() => onRowClick?.(item)}
                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                  isSelected
                    ? 'border-primary-400 bg-primary-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="space-y-1.5">
                  {columns
                    .filter((c) => !c.hideOnMobile)
                    .map((col) => (
                      <div key={col.key} className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 text-xs">{col.header}</span>
                        <div className="text-right">{col.render(item)}</div>
                      </div>
                    ))}
                </div>
                {(onPreview || onEdit) && (
                  <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
                    {onPreview && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPreview(item);
                        }}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-600 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        <Eye size={14} /> View
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(item);
                        }}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-600 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ─── Desktop: Table Layout ───── */
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {columns.map((col) => {
                  const sortKey = getColumnSortKey(col);
                  const isActive = activeSortBy === sortKey;
                  const ariaSort: 'ascending' | 'descending' | 'none' | undefined = col.sortable
                    ? isActive
                      ? activeSortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                    : undefined;
                  return (
                    <th
                      key={col.key}
                      aria-sort={ariaSort}
                      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${alignClass(col.align)} ${col.width || ''}`}
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col)}
                          className={`inline-flex items-center gap-1 select-none uppercase tracking-wider ${
                            col.align === 'right'
                              ? 'flex-row-reverse'
                              : col.align === 'center'
                                ? 'mx-auto'
                                : ''
                          } hover:text-gray-700 transition-colors`}
                        >
                          <span>{col.header}</span>
                          {renderSortIndicator(col)}
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
                {(onPreview || onEdit) && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayItems.map((item) => {
                const key = getKey(item);
                const isSelected = selectedId === key;
                return (
                  <tr
                    key={key}
                    onClick={() => onRowClick?.(item)}
                    className={`transition-colors cursor-pointer ${
                      isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 ${alignClass(col.align)} ${col.width || ''}`}
                      >
                        {col.render(item)}
                      </td>
                    ))}
                    {(onPreview || onEdit) && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {onPreview && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPreview(item);
                              }}
                              className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                              title="View details"
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          {onEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(item);
                              }}
                              className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination + page size */}
      {(showPagination || showPageSizeSelector) && (
        <div className="flex items-center justify-between mt-4 px-1 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {total != null && (
              <p className="text-xs text-gray-500">
                Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}–
                {Math.min(page * pageSize, total)} of {total}
              </p>
            )}
            {showPageSizeSelector && (
              <label className="text-xs text-gray-500 flex items-center gap-1">
                <span>Per page:</span>
                <select
                  aria-label="Page size"
                  value={pageSize}
                  onChange={(e) => onPageSizeChange!(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {pageSizeOptions!.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {showPagination && onPageChange && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    aria-label={`Page ${pageNum}`}
                    aria-current={page === pageNum ? 'page' : undefined}
                    className={`w-8 h-8 text-xs rounded-lg transition-colors ${
                      page === pageNum
                        ? 'bg-primary-600 text-white font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                aria-label="Next page"
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Display Helpers ────────────────────────────────────────────

/** Status badge with dynamic color */
export function StatusBadge({ label, color }: { label: string; color?: string | null }) {
  const colorMap: Record<string, string> = {
    red: 'bg-red-100 text-red-700',
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    orange: 'bg-orange-100 text-orange-700',
    purple: 'bg-purple-100 text-purple-700',
    pink: 'bg-pink-100 text-pink-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  const classes = colorMap[color?.toLowerCase() || ''] || 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${classes}`}
    >
      {label}
    </span>
  );
}

/** Currency display */
export function CurrencyValue({ amount, className }: { amount: number; className?: string }) {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return <span className={className || 'text-gray-900 font-medium'}>{formatted}</span>;
}
